import { RecurrenceTranslations } from "@cents/translations-utils"
import { standardFields } from "../../../assets/fields/standard-fields"
import { ContractStatus } from "../../../enums/contract-status"
import { DonationType } from "../../../enums/donation-type"
import { PaymentMethodType } from "../../../enums/payment-method-type"
import { Charity } from "../../../types/charity/charity"
import { Contract } from "../../../types/contract"
import { Field, TableField } from "../../../types/table-field"
import { contractStatusStrings, contractTypeStrings, paymentMethodTypeStrings } from "../../../utils/strings/DonationStrings"
import { translations } from "../../../utils/translations/translations-manager"
import { useCustomFields } from "../../custom-fields/CustomFieldsHook"
import { formatAmount } from "../../../utils/strings/amount-format"

export function useContractsTableFields() {

  const selectFieldsToDisplay = (fields: Field[], fieldNames: string[]): Field[] => {
    return fields.filter((field) => fieldNames.includes(field.name ?? ''))
  }

  function mapFieldsToTableFields(fields: Field[]): TableField[] {
    return fields.map(field => {
      let formattingRule: (value: any) => string

      switch (field.type) {
        case "text":
        case "fiscal_code":
        case "phone_number":
        case "text_multiline":
        case "email":
          formattingRule = (value: any) => value as string
          break
        case "date":
          formattingRule = (value: any) => (new Date(value as string)).toLocaleString(translations.getLanguage())
          break;
        case "amount":
          formattingRule = (value: any) => formatAmount((value as number) / 100)
          break;
        case "number":
          formattingRule = (value: any) => (value as number).toString()
          break
        case "checkbox":
          formattingRule = (value: any) => (value as boolean ? 'SÌ' : 'NO')
          break
        case "type":
          formattingRule = (value: any) => {
            if (field.name === "status") {
              return contractStatusStrings[value as ContractStatus]()
            } else if (field.name === "contractType") {
              return contractTypeStrings[value as DonationType]()
            } else if (field.name === "paymentMethodType") {
              return paymentMethodTypeStrings[value as PaymentMethodType]()
            } else {
              return value as string
            }
          }
          break
        default:
          formattingRule = (value: any) => value as string
      }

      return {
        ...field,
        formattingRule,
      }
    })
  }

  const { get } = useCustomFields()
  const defaultFields = ['_id', 'donorFullName', 'donorEmail', 'donationAmount', 'contractType', 'createdAt', 'operatorId', 'paymentMethodType', 'status']

  const getCustomFields = async (charity: Charity) => {
    const customFields = await get()
    const customFieldsToDisplay = selectFieldsToDisplay(customFields, charity?.contractsTableFields && charity.contractsTableFields.length !== 0 ? charity?.contractsTableFields : defaultFields)
    const mappedCustomFields = mapFieldsToTableFields(customFieldsToDisplay)
    return mappedCustomFields
  }

  const getStandardFields = async (charity: Charity) => {
    const standardFieldsToDisplay = selectFieldsToDisplay(standardFields, charity?.contractsTableFields && charity.contractsTableFields.length !== 0 ? ['_id', ...charity?.contractsTableFields] : defaultFields)
    const mappedStandardFields = mapFieldsToTableFields(standardFieldsToDisplay)
    return mappedStandardFields
  }

  const filterAndFormatContractsByFields = (contract: Contract, fields: TableField[]): Record<string, any> => {
    const filteredRecords = Object.fromEntries(
      fields.map(field => {
        const key = field.name ?? ''
        let stringValue = ''
        if (key === 'donationAmount' && key in contract && contract.contractType === DonationType.recurring && contract.subscriptionRecurrence) {
          stringValue = field.formattingRule ? `${field.formattingRule(contract[key])} ${RecurrenceTranslations.getRecurrenceString(translations, { interval: contract.subscriptionRecurrence, count: contract.subscriptionRecurrenceCount ?? 1 }).toLowerCase()}` 
          : `${contract[key]} ${RecurrenceTranslations.getRecurrenceString(translations, { interval: contract.subscriptionRecurrence, count: contract.subscriptionRecurrenceCount ?? 1 }).toLowerCase()}` 
        } else {
          stringValue = key in contract && field.formattingRule ? field.formattingRule(contract[key]) : ''
        }
        return [key, stringValue]
      }).sort(([keyA], [keyB]) => {
        // TODO: implement ordering function
        if (keyA === "_id") return -1
        if (keyB === "_id") return 1
        const indexA = fields.findIndex(field => field.name === keyA)
        const indexB = fields.findIndex(field => field.name === keyB)
        return indexA - indexB
      })
    )
    return filteredRecords
  }

  return { selectFieldsToDisplay, mapFieldsToTableFields, filterAndFormatContractsByFields, getCustomFields, getStandardFields }
}

export default useContractsTableFields